import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  TextField,
  Grid,
  Avatar,
  Alert,
  Chip,
  Fade,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  ReportProblem as ReportProblemIcon,
  Addchart as AddchartIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import apiAxiosInstance, { aiAxiosInstance } from '../../utils/axiosInstance';
import RosterPanelEnhanced from './RosterPanelEnhanced';
import CasePanel from './CasePanel';
import StrategyPicker from './StrategyPicker';
import FormattedMessage from './Chat/FormattedMessage';
import AddStudentDialog from './Dialogs/AddStudentDialog';
import AddGroupDialog from './Dialogs/AddGroupDialog';
import IncidentLogger from './IncidentLogger';
import FrequencyCounter from './DataCollection/FrequencyCounter';
import DurationTimer from './DataCollection/DurationTimer';
import SectionEmptyState from '../Common/SectionEmptyState';
import type { BehaviorCase, BehaviorCoachContext, BehaviorNote } from '../../Types';
import { buildAiAuthHeaders, consumeSSEStream } from '../../hooks/useSSEStream';
import { BEHAVIOR_STARTER_PROMPTS } from '../../constants/emptyStatePrompts';

interface ConversationSummary {
  date: string;
  summary: string;
  keyPoints: string[];
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

const BehaviorPageEnhanced: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<BehaviorCase[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiReply, setAiReply] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedScope, setSelectedScope] = useState<'student' | 'group' | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [ctx, setCtx] = useState<BehaviorCoachContext>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rosterRefreshTrigger, setRosterRefreshTrigger] = useState(0);
  const [caseDataRefreshKey, setCaseDataRefreshKey] = useState(0);
  const [incidentLoggerOpen, setIncidentLoggerOpen] = useState(false);
  const [frequencyOpen, setFrequencyOpen] = useState(false);
  const [durationOpen, setDurationOpen] = useState(false);
  
  // Conversational mode - enabled by default
  const [conversationalMode, setConversationalMode] = useState(true);
  
  // Conversation summaries for progress tracking
  const [conversationSummaries, setConversationSummaries] = useState<ConversationSummary[]>([]);
  
  // Track if we need to show contextual greeting
  const [hasShownGreeting, setHasShownGreeting] = useState(false);
  
  // Track if we're currently loading a case
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiReply]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setLoadError(null);
        const res = await apiAxiosInstance.get('/api/behavior/cases');
        setCases(res.data || []);
      } catch (e) {
        console.error('Failed to load cases', e);
        setLoadError('Could not load behavior cases. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load conversation data when case changes
  useEffect(() => {
    const loadCaseData = async () => {
      if (!ctx.case?._id) {
        setConversationSummaries([]);
        setMessages([]);
        setHasShownGreeting(false);
        return;
      }
      
      setIsLoadingCase(true);
      try {
        // Load conversation history first
        const loadedMessages = await loadChatHistory(ctx.case._id);
        const summaries = await loadConversationSummaries(ctx.case._id);
        
        // If we have chat history, use it
        if (loadedMessages.length > 0) {
          setMessages(loadedMessages);
          setHasShownGreeting(true);
        } else if (!hasShownGreeting) {
          // Otherwise show greeting
          await showContextualGreeting(summaries);
          setHasShownGreeting(true);
        }
      } catch (e) {
        console.error('Failed to load case data', e);
        setLoadError('Could not load conversation history for this case.');
      } finally {
        setIsLoadingCase(false);
      }
    };
    
    loadCaseData();
  }, [ctx.case?._id]); // Only run when case changes

  const loadChatHistory = async (caseId: string): Promise<ChatMessage[]> => {
    try {
      console.log('[Behavior] Loading chat history for case:', caseId);
      
      const res = await apiAxiosInstance.get(`/api/behavior/cases/${caseId}/notes`);
      const notes: BehaviorNote[] = res.data || [];

      const chatMessages: ChatMessage[] = notes
        .filter((n) => n.type === 'chat_message')
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((n) => ({
          role: (n.senderRole === 'user' ? 'user' : 'assistant') as ChatMessage['role'],
          content: n.content || '',
          timestamp: n.createdAt,
        }));
      
      console.log('[Behavior] Loaded chat messages:', chatMessages.length);
      return chatMessages;
    } catch (e) {
      console.error('[Behavior] Failed to load chat history', e);
      return [];
    }
  };

  const loadConversationSummaries = async (caseId: string): Promise<ConversationSummary[]> => {
    try {
      console.log('[Behavior] Loading conversation summaries for case:', caseId);
      
      const res = await apiAxiosInstance.get(`/api/behavior/cases/${caseId}/notes`);
      const notes: BehaviorNote[] = res.data || [];

      const summaries: ConversationSummary[] = notes
        .filter((n) => n.type === 'conversation_summary' || n.type === 'ai_summary')
        .map((n) => ({
          date: n.createdAt,
          summary: n.content || '',
          keyPoints: n.keyPoints || [],
        }))
        .slice(0, 10);
      
      console.log('[Behavior] Parsed summaries:', summaries.length);
      setConversationSummaries(summaries);
      return summaries;
    } catch (e) {
      console.error('[Behavior] Failed to load conversation summaries', e);
      return [];
    }
  };

  const showContextualGreeting = async (summaries: ConversationSummary[]) => {
    if (!ctx.case) return;
    
    const hasPreviousConversation = summaries.length > 0;
    const lastInteraction = summaries[0];
    
    let greetingContent = '';
    
    if (hasPreviousConversation && lastInteraction) {
      const dateStr = new Date(lastInteraction.date).toLocaleDateString();
      greetingContent = `Last time we talked on ${dateStr}, we discussed: "${lastInteraction.summary.substring(0, 80)}..." What's been happening since then?`;
    } else {
      greetingContent = `Hi! I'm here to help with behavior strategies for ${selectedName}. What would you like to work on today?`;
    }
    
    // Save greeting as first message
    if (ctx.case._id) {
      await saveChatMessage(ctx.case._id, 'assistant', greetingContent);
    }
    
    setMessages([{ role: 'assistant', content: greetingContent }]);
  };

  const targetBehaviorLabel =
    ctx.case?.title || (selectedName ? `behavior for ${selectedName}` : 'target behavior');

  const saveObservationNote = async (content: string) => {
    if (!ctx.case?._id) return;
    try {
      await apiAxiosInstance.post(`/api/behavior/cases/${ctx.case._id}/notes`, {
        type: 'observation',
        content,
      });
      setCaseDataRefreshKey((k) => k + 1);
    } catch (e) {
      console.error('[Behavior] Failed to save observation', e);
      setError('Could not save observation to the case.');
    }
  };

  const saveChatMessage = async (caseId: string, role: 'user' | 'assistant', content: string) => {
    try {
      await apiAxiosInstance.post(`/api/behavior/cases/${caseId}/notes`, {
        type: 'chat_message',
        senderRole: role,
        content: content,
      });
      console.log('[Behavior] Saved chat message:', role, content.substring(0, 50));
    } catch (e) {
      console.error('[Behavior] Failed to save chat message', e);
      setError('Message could not be saved. Your reply may not appear after refresh.');
    }
  };

  const handleSelect = (scope: 'student' | 'group', id: string, name: string) => {
    setSelectedScope(scope);
    setSelectedId(id);
    setSelectedName(name);
    setMessages([]);
    setHasShownGreeting(false);
    setError(null);
  };

  const generateConversationSummary = useCallback(async (userMessage: string, aiResponse: string) => {
    if (!ctx.case?._id) return;
    
    try {
      const response = await fetch(`${aiAxiosInstance.defaults.baseURL}/behavior/coach/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userMessage,
          aiResponse,
          previousSummaries: conversationSummaries.slice(0, 3),
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        await apiAxiosInstance.post(`/api/behavior/cases/${ctx.case._id}/notes`, {
          type: 'conversation_summary',
          content: data.summary,
          keyPoints: data.keyPoints,
        });
        
        const newSummary: ConversationSummary = {
          date: new Date().toISOString(),
          summary: data.summary,
          keyPoints: data.keyPoints,
        };
        setConversationSummaries(prev => [newSummary, ...prev]);
      }
    } catch (e) {
      console.error('[Behavior] Failed to generate conversation summary', e);
    }
  }, [ctx.case?._id, conversationSummaries]);

  const handleSendMessage = async () => {
    if (!aiInput.trim()) return;
    
    if (!selectedId) {
      setError('Please select a student or group from the roster first');
      return;
    }

    const userContent = aiInput.trim();
    const userMsg: ChatMessage = { role: 'user', content: userContent };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setAiInput('');
    setAiReply('');
    setError(null);

    // Save user message
    if (ctx.case?._id) {
      await saveChatMessage(ctx.case._id, 'user', userContent);
    }

    try {
      let full = '';
      let aiSummary: string | null = null;

      await consumeSSEStream({
        url: `${aiAxiosInstance.defaults.baseURL}/behavior/coach/stream`,
        headers: buildAiAuthHeaders(),
        body: JSON.stringify({
          message: userContent,
          scope: selectedScope,
          case: ctx.case || null,
          notes: ctx.notes || [],
          strategies: ctx.strategies || [],
          messages: updatedMessages,
          conversationalMode,
          conversationSummaries: conversationSummaries.slice(0, 5),
        }),
        onEvent: (data) => {
          if (typeof data.content === 'string') {
            full += data.content;
            setAiReply((prev) => prev + data.content);
          }
          if (
            data.event === 'done' &&
            typeof data.ai_summary === 'string' &&
            data.ai_summary.trim()
          ) {
            aiSummary = data.ai_summary.trim();
          }
        },
      });

      // Save AI response
      if (ctx.case?._id) {
        await saveChatMessage(ctx.case._id, 'assistant', full);
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: full }]);
      setAiReply('');

      // Save AI summary as note
      if (aiSummary && ctx.case) {
        try {
          await apiAxiosInstance.post(`/api/behavior/cases/${ctx.case._id}/notes`, {
            type: 'ai_summary',
            content: aiSummary,
          });
        } catch {}
      }
      
      // Generate conversation summary for progress tracking
      if (conversationalMode) {
        await generateConversationSummary(userContent, full);
      }
    } catch (e: any) {
      setError(e?.message || 'Error sending message');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        Behavior Management
        {selectedName && (
          <Chip
            label={selectedName}
            color="primary"
            size="small"
            sx={{ ml: 1 }}
            icon={selectedScope === 'student' ? <PersonIcon /> : <AIIcon />}
          />
        )}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Sidebar - Roster */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <RosterPanelEnhanced
            onSelect={handleSelect}
            selectedId={selectedId}
            selectedScope={selectedScope}
            onAddStudent={() => setAddStudentOpen(true)}
            onAddGroup={() => setAddGroupOpen(true)}
            refreshTrigger={rosterRefreshTrigger}
          />
        </Grid>

        {/* Main Chat Area */}
        <Grid item xs={12} md={6} sx={{ height: '100%' }}>
          <Paper
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {/* Chat Mode Toggle */}
            {selectedId && (
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conversationalMode}
                      onChange={(e) => setConversationalMode(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ChatIcon fontSize="small" color={conversationalMode ? 'primary' : 'disabled'} />
                      <Typography variant="caption">
                        {conversationalMode ? 'Coach Mode - Direct, practical responses' : 'Detailed Mode - Comprehensive responses'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {ctx.case && (
              <Box
                sx={{
                  p: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReportProblemIcon />}
                  onClick={() => setIncidentLoggerOpen(true)}
                >
                  Log incident
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddchartIcon />}
                  onClick={() => setFrequencyOpen(true)}
                >
                  Frequency count
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TimerIcon />}
                  onClick={() => setDurationOpen(true)}
                >
                  Duration timer
                </Button>
              </Box>
            )}

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setLoadError(null)}
                >
                  {loadError}
                </Alert>
              )}

              {!selectedId && (
                <SectionEmptyState
                  icon={<PersonIcon sx={{ fontSize: 48 }} />}
                  title="Select a student or group"
                  description="Choose someone from the roster on the left, then create or open a case on the right to start coaching."
                />
              )}

              {selectedId && !ctx.case && !isLoadingCase && (
                <SectionEmptyState
                  icon={<AIIcon sx={{ fontSize: 48 }} />}
                  title="Open or create a case"
                  description={`Create a case for ${selectedName} in the panel on the right. Cases keep chat history, incidents, and strategies together.`}
                />
              )}

              {isLoadingCase && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading conversation history...
                </Alert>
              )}

              {ctx.case && messages.length === 0 && !isLoadingCase && !aiReply && (
                <SectionEmptyState
                  icon={<AIIcon sx={{ fontSize: 48 }} />}
                  title="AI Behavior Coach"
                  description={`Ask about strategies, data collection, or family communication for ${selectedName}.`}
                  prompts={BEHAVIOR_STARTER_PROMPTS.map((label) => ({
                    label,
                    onClick: () => setAiInput(label),
                  }))}
                />
              )}

              {messages.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}
                >
                  <Avatar
                    sx={{
                      bgcolor: m.role === 'assistant' ? 'primary.main' : 'secondary.main',
                      mr: 2,
                      width: 32,
                      height: 32,
                      fontSize: 14,
                    }}
                  >
                    {m.role === 'assistant' ? <AIIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <FormattedMessage content={m.content} role={m.role} />
                  </Box>
                </Box>
              ))}

              {aiReply && (
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      mr: 2,
                      width: 32,
                      height: 32,
                      fontSize: 14,
                    }}
                  >
                    <AIIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <FormattedMessage content={aiReply} role="assistant" />
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          animation: 'pulse 1s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  placeholder={
                    selectedId
                      ? conversationalMode 
                        ? "Share an observation or ask a question..."
                        : "Ask about behavior strategies..."
                      : "Select a student/group first..."
                  }
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!selectedId || isLoadingCase}
                  multiline
                  maxRows={3}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!selectedId || !aiInput.trim() || isLoadingCase}
                  startIcon={<SendIcon />}
                  sx={{ minWidth: 100 }}
                >
                  Send
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Right Sidebar - Case Details */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <CasePanel 
            scope={selectedScope} 
            id={selectedId} 
            name={selectedName}
            onLoadContext={setCtx}
            onCaseCreated={() => setRosterRefreshTrigger(prev => prev + 1)}
            dataRefreshKey={caseDataRefreshKey}
          />
        </Grid>
      </Grid>

      {/* Strategy Picker */}
      <StrategyPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={async (s) => {
          try {
            if (!ctx.case) return;
            await apiAxiosInstance.post(`/api/behavior/cases/${ctx.case._id}/strategies`, {
              strategy: s,
            });
            setCaseDataRefreshKey((k) => k + 1);
          } catch {}
        }}
      />

      {/* Add Student Dialog */}
      <AddStudentDialog
        open={addStudentOpen}
        onClose={() => {
          setAddStudentOpen(false);
          setRosterRefreshTrigger(prev => prev + 1);
        }}
        onStudentAdded={(student) => {
          setRosterRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Add Group Dialog */}
      <AddGroupDialog
        open={addGroupOpen}
        onClose={() => {
          setAddGroupOpen(false);
          setRosterRefreshTrigger(prev => prev + 1);
        }}
        onGroupAdded={(group) => {
          setRosterRefreshTrigger(prev => prev + 1);
        }}
      />

      {ctx.case && (
        <IncidentLogger
          open={incidentLoggerOpen}
          onClose={() => {
            setIncidentLoggerOpen(false);
            setCaseDataRefreshKey((k) => k + 1);
          }}
          caseId={ctx.case._id}
          studentName={selectedName}
        />
      )}

      <Dialog open={frequencyOpen} onClose={() => setFrequencyOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <FrequencyCounter
            targetBehavior={targetBehaviorLabel}
            onSave={(count, durationMin, notes) => {
              const lines = [
                `Frequency count — ${targetBehaviorLabel}`,
                `Count: ${count} occurrence(s) over ${durationMin} minute(s)`,
              ];
              if (notes.trim()) lines.push(`Notes: ${notes.trim()}`);
              void saveObservationNote(lines.join('\n'));
              setFrequencyOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={durationOpen} onClose={() => setDurationOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <DurationTimer
            targetBehavior={targetBehaviorLabel}
            onSave={(durationMs, notes) => {
              const totalSec = Math.floor(durationMs / 1000);
              const min = Math.floor(totalSec / 60);
              const sec = totalSec % 60;
              const lines = [
                `Duration observation — ${targetBehaviorLabel}`,
                `Duration: ${min}m ${sec}s`,
              ];
              if (notes.trim()) lines.push(`Notes: ${notes.trim()}`);
              void saveObservationNote(lines.join('\n'));
              setDurationOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BehaviorPageEnhanced;
